/**
 * Browser and third-party errors the production Sentry client drops before sending.
 *
 * Every entry names an event no code of ours raised or can act on. Messages are
 * anchored exact matches and URLs name a host we do not serve, so an error thrown
 * from our own bundles is never swallowed by a looser neighbour.
 */
import type { Event } from '@sentry/react';

const ignoreErrors: RegExp[] = [
  // Chrome < 110 wording of the benign ResizeObserver loop notice. It reaches
  // window.onerror with no error object, so it carries no stack to follow. The
  // SDK's defaults only list the newer "loop completed with undelivered
  // notifications" wording.
  /^ResizeObserver loop limit exceeded$/,
  // Chrome's rejection for a body read (json/text/blob) whose AbortController was
  // aborted: a deliberate cancellation, and the DOMException has no stack.
  /^AbortError: The user aborted a request\.$/,
  // An email link scanner (Outlook Safe Links style) that opens shared links
  // and calls back into its own injected bridge, which rejects with this plain
  // object. Our code has no such method dispatch; 10 events, 0 users on /proof.
  /^Non-Error promise rejection captured with value: Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+$/,
];

const denyUrls: RegExp[] = [
  // Hotjar's recorder (script.hotjar.com/modules.<hash>.js) JSON.parses its own
  // XHR responses; an HTML error page there is Hotjar's failure, not ours. The
  // SDK tests the innermost frame, so a Hotjar callback that throws inside our
  // code still reports.
  /^https:\/\/(?:script|static)\.hotjar\.com\//,
  // The Meta Pixel (fbevents.js and the config it loads) and the CAPI parameter
  // builder it injects from S3, which throws "Fail to read from cookie: The
  // operation is insecure" where cookies are blocked (8080-AI-CZ).
  /^https:\/\/connect\.facebook\.net\//,
  /^https:\/\/capi-automation\.s3[\w.-]*\.amazonaws\.com\//,
  // Google Tag Manager and the analytics and ads tags it is configured to load.
  /^https:\/\/www\.googletagmanager\.com\//,
  /^https:\/\/(?:[\w-]+\.)*google-analytics\.com\//,
  /^https:\/\/(?:[\w-]+\.)*(?:googleadservices\.com|doubleclick\.net)\//,
  // The rest of index.html's vendors: the Font Awesome kit and TrafficJunky ad tags.
  /^https:\/\/(?:kit|ka-p)\.fontawesome\.com\//,
  /^https:\/\/(?:[\w-]+\.)*trafficjunky\.(?:net|com)\//,
];

/** Spread into Sentry.init; the SDK's event filters apply both lists. */
export const SENTRY_NOISE_FILTERS = { ignoreErrors, denyUrls };

/** A frame that ran one of our bundles: same origin as the page, under /static/js/. */
const isAppFrame = (filename: string | undefined, appOrigin: string): boolean => {
  try {
    const url = new URL(filename ?? '');

    return url.origin === appOrigin && url.pathname.startsWith('/static/js/');
  } catch {
    return false; // "<anonymous>", "[native code]" or no filename at all
  }
};

/**
 * True when no stack frame of the exception ran our code: every frame is a
 * vendor script, inline script injected into the page (its filename is the page
 * URL, not a bundle) or a chrome-/moz-/safari-extension:// content script. The
 * denyUrls test above reads only the innermost frame; this reads them all, so a
 * recursion inside an injected script (8080-AI-D0) is dropped too. An exception
 * without frames is kept: there is nothing to judge it by.
 */
export const isThirdPartyOnlyEvent = (event: Event, appOrigin: string = window.location.origin): boolean => {
  const frames = (event.exception?.values ?? []).flatMap((value) => value.stacktrace?.frames ?? []);

  return frames.length > 0 && !frames.some((frame) => isAppFrame(frame.filename, appOrigin));
};
